<template>
  <v-container>
    <!-- pageHeader -->
    <v-row>
      <v-col cols="8">
        <h1>Notifications</h1>
      </v-col>
      <v-col cols="4" align="end">
        <v-btn
          color="primary"
          depressed
          @click="$router.go(-1)"
          :loading="$store.state.loading"
          :disabled="$store.state.loading"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
    </v-row>
    <!-- pageHeader -->

    <v-row>
      <!-- LEFT: Notifications -->
      <v-col cols="12" md="7">
        <transition name="fade" mode="out-in" appear>
          <v-card
            outlined
            class="px-4 py-2 pb-4"
            :loading="$store.state.loading"
          >
            <div class="d-flex align-center mb-3">
              <h3 class="title">
                Notifications
                {{
                  adminNotifications && adminNotifications.length
                    ? adminNotifications.length
                    : ""
                }}
              </h3>
              <span class="inline-flex text-caption font-italic ml-12 mb-1"
                >sorted by date added (newest at top)</span
              >
              <v-spacer />
              <v-btn
                v-if="adminNotifications && adminNotifications.length"
                small
                outlined
                color="primary"
                @click="archiveAllNotifications"
                :loading="$store.state.loading"
                :disabled="$store.state.loading"
                >Archive All</v-btn
              >
            </div>

            <v-data-iterator
              :items="adminNotifications"
              item-key="id"
              no-data-text="All Notifications Archived"
              :loading="$store.state.loading"
              :items-per-page="6"
              :footer-props="{
                'items-per-page-options': [15, 25, 50, -1],
                'show-current-page': true,
              }"
            >
              <template v-slot:default="{ items }">
                <v-row dense>
                  <v-col
                    cols="12"
                    sm="6"
                    md="6"
                    v-for="(item, index) in items"
                    :key="index"
                  >
                    <v-card
                      outlined
                      class="grey lighten-2 d-flex flex-column"
                      :loading="$store.state.loading"
                      height="100%"
                    >
                      <v-card-text class="text--primary flex-grow-1">
                        <p>{{ item.text }}</p>
                      </v-card-text>
                      <v-card-actions>
                        <div class="caption pl-2">
                          {{ formatDate(item.dateAdded) }}
                        </div>
                        <v-spacer />
                        <v-btn
                          :loading="$store.state.loading"
                          :disabled="$store.state.loading"
                          small
                          depressed
                          color="primary"
                          @click="archiveNotification(item)"
                          >archive</v-btn
                        >
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </v-data-iterator>
          </v-card>
        </transition>
      </v-col>

      <!-- RIGHT: Task Manager -->
      <v-col cols="12" md="5">
        <v-card outlined>
          <v-card-title class="py-3">
            <span class="font-weight-medium">Task Manager</span>
            <v-spacer />
            <v-btn
              v-if="canUseTaskManager"
              small
              color="primary"
              depressed
              @click="openCreateTask"
            >
              Create Task
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <!-- Review toggle: ONLY task manager users -->
            <v-switch
              v-if="canUseTaskManager"
              v-model="showCompletedTasks"
              inset
              dense
              label="Show completed tasks"
              class="mt-0"
              :disabled="$store.state.loading"
            />

            <div class="font-weight-medium mb-2">My Open Tasks</div>

            <v-alert
              outlined
              type="info"
              v-if="!tasksMyOpen || !tasksMyOpen.length"
            >
              No open tasks assigned to you.
            </v-alert>

            <v-list two-line v-else>
              <v-list-item
                v-for="t in tasksMyOpen"
                :key="t.id"
                @click="openTask(t)"
              >
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ t.title || "(Untitled task)" }}
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <span v-if="t.dueDate"
                      >Due: {{ fmtDate(t.dueDate) }} •
                    </span>
                    <span v-if="t.assignedByName"
                      >Assigned by {{ t.assignedByName }}</span
                    >
                  </v-list-item-subtitle>

                  <div class="mt-2" v-if="t.instructions">
                    <small class="grey--text text--darken-1">{{
                      t.instructions
                    }}</small>
                  </div>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn
                    x-small
                    color="success"
                    depressed
                    :loading="loadingTaskId === t.id"
                    :disabled="!!loadingTaskId || $store.state.loading"
                    @click.stop="completeTask(t)"
                  >
                    Complete
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <template v-if="canUseTaskManager">
              <v-divider class="my-4" />
              <div class="font-weight-medium mb-2">All Open Tasks</div>

              <v-alert
                outlined
                type="info"
                v-if="!tasksAllOpen || !tasksAllOpen.length"
              >
                No open tasks.
              </v-alert>

              <v-list two-line v-else>
                <v-list-item
                  v-for="t in tasksAllOpen"
                  :key="t.id"
                  @click="openTask(t)"
                >
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ t.title || "(Untitled task)" }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <span v-if="t.assignedToName"
                        >To: {{ t.assignedToName }} •
                      </span>
                      <span v-if="t.dueDate"
                        >Due: {{ fmtDate(t.dueDate) }}</span
                      >
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <!-- All Completed (lazy-loaded) -->
              <template v-if="showCompletedTasks">
                <v-divider class="my-4" />
                <div class="font-weight-medium mb-2">All Completed Tasks</div>

                <v-alert
                  outlined
                  type="info"
                  v-if="!tasksAllCompleted || !tasksAllCompleted.length"
                >
                  No completed tasks.
                </v-alert>

                <v-list two-line v-else>
                  <v-list-item
                    v-for="t in tasksAllCompleted"
                    :key="t.id"
                    @click="openTask(t)"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ t.title || "(Untitled task)" }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span v-if="t.assignedToName"
                          >To: {{ t.assignedToName }} •
                        </span>
                        <span v-if="t.completedAt"
                          >Completed: {{ fmtDate(t.completedAt) }}</span
                        >
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </template>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!--  TASK DIALOG  -->
    <v-dialog v-model="taskDialogOpen" max-width="900">
      <v-card>
        <v-toolbar
          dense
          flat
          :color="taskDialogIsCreate ? 'primary' : 'grey darken-3'"
          dark
        >
          <v-toolbar-title>
            {{ taskDialogIsCreate ? "Create Task" : "Task Details" }}
          </v-toolbar-title>
          <v-spacer />
          <v-chip v-if="!taskDialogIsCreate" small outlined class="mr-3">
            {{ taskDialogModeLabel }}
          </v-chip>
          <v-btn icon @click="closeTaskDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6">
          <v-row>
            <!-- Left: Fields -->
            <v-col cols="12" md="7">
              <v-form ref="taskDialogForm" v-model="taskDialogValid">
                <v-text-field
                  v-model="taskDraft.title"
                  label="Title"
                  outlined
                  dense
                  :disabled="!canEditTaskDraft"
                  :rules="[(v) => !!v || 'Required']"
                />

                <v-textarea
                  v-model="taskDraft.instructions"
                  label="Instructions"
                  outlined
                  dense
                  rows="6"
                  :disabled="!canEditTaskDraft"
                />

                <!-- Due date picker -->
                <v-menu
                  v-model="dueMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  :disabled="!canEditTaskDraft"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="taskDraftDueDisplay"
                      label="Due date"
                      outlined
                      dense
                      readonly
                      clearable
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!canEditTaskDraft"
                      @click:clear="clearDueDate"
                    />
                  </template>

                  <v-date-picker
                    v-model="dueDatePicker"
                    :min="todayYmd"
                    @input="onPickDueDate"
                  />
                </v-menu>

                <!-- Assign to -->
                <v-autocomplete
                  v-if="canUseTaskManager"
                  v-model="selectedStaffUid"
                  :items="staffOptions"
                  item-value="uid"
                  item-text="fullName"
                  label="Assign to"
                  outlined
                  dense
                  clearable
                  :disabled="!canEditTaskDraft"
                  :rules="[(v) => !!v || 'Required']"
                >
                  <template v-slot:item="{ item }">
                    <div class="d-flex flex-column">
                      <div class="font-weight-medium">{{ item.fullName }}</div>
                      <div class="text-caption grey--text">
                        {{ item.role
                        }}<span v-if="item.title"> • {{ item.title }}</span
                        ><span v-if="item.email"> • {{ item.email }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-slot:selection="{ item }">
                    <span>{{ item.fullName }}</span>
                  </template>
                </v-autocomplete>

                <v-alert v-else dense outlined type="info" class="mt-2">
                  Assigned to:
                  <b>{{
                    (selectedTask && selectedTask.assignedToName) || "—"
                  }}</b>
                </v-alert>

                <!-- Audit/status -->
                <div
                  v-if="selectedTask && !taskDialogIsCreate"
                  class="mt-3 text-caption grey--text"
                >
                  <div v-if="selectedTask.assignedAt">
                    Assigned: <b>{{ fmtDate(selectedTask.assignedAt) }}</b>
                    <span v-if="selectedTask.assignedByName">
                      • By: <b>{{ selectedTask.assignedByName }}</b>
                    </span>
                  </div>
                  <div v-if="selectedTask.completedAt">
                    Completed: <b>{{ fmtDate(selectedTask.completedAt) }}</b>
                    <span v-if="selectedTask.completedByName">
                      • By: <b>{{ selectedTask.completedByName }}</b>
                    </span>
                  </div>
                </div>
              </v-form>
            </v-col>

            <!-- Right: Attachments -->
            <v-col cols="12" md="5">
              <div class="font-weight-medium mb-2">Attachments</div>

              <v-file-input
                v-model="attachmentFiles"
                multiple
                outlined
                dense
                prepend-icon="mdi-paperclip"
                label="Add attachments"
                :disabled="uploadingAttachments || !canUploadAttachments"
              />

              <v-btn
                class="mt-2"
                color="primary"
                depressed
                block
                :loading="uploadingAttachments"
                :disabled="
                  uploadingAttachments ||
                    !attachmentFiles ||
                    !attachmentFiles.length ||
                    !canUploadAttachments
                "
                @click="uploadAttachments"
              >
                Upload
              </v-btn>

              <v-alert
                dense
                outlined
                type="info"
                class="mt-3"
                v-if="taskDialogIsCreate"
              >
                Tip: you can select files now. When you click <b>Create</b>, the
                task will be created first, then files will upload.
              </v-alert>

              <v-divider class="my-4" />

              <v-alert outlined type="info" v-if="!taskAttachments.length">
                No attachments yet.
              </v-alert>

              <v-list dense v-else>
                <v-list-item v-for="(a, idx) in taskAttachments" :key="idx">
                  <v-list-item-content>
                    <v-list-item-title class="text-sm font-weight-medium">
                      <a :href="a.url" target="_blank" rel="noreferrer">
                        {{ a.name || "Attachment" }}
                      </a>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <span v-if="a.uploadedAt"
                        >Uploaded: {{ fmtDate(a.uploadedAt) }} •
                      </span>
                      <span v-if="a.uploadedByName">{{
                        a.uploadedByName
                      }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-btn
            v-if="
              !taskDialogIsCreate &&
                isAssigneeOfSelectedTask &&
                selectedTask &&
                selectedTask.status === 'open'
            "
            color="success"
            depressed
            :loading="loadingTaskId === selectedTask.id"
            :disabled="!!loadingTaskId || $store.state.loading"
            @click="completeTask(selectedTask)"
          >
            Complete Task
          </v-btn>
          <v-btn
            v-if="
              !taskDialogIsCreate &&
                canUseTaskManager &&
                selectedTask &&
                selectedTask.status === 'open'
            "
            color="warning"
            depressed
            :disabled="savingTask || !!loadingTaskId"
            @click="cancelTask(selectedTask)"
          >
            Cancel Task
          </v-btn>

          <!--  Hiding delete for now
          <v-btn
            v-if="!taskDialogIsCreate && canUseTaskManager && selectedTask"
            color="error"
            depressed
            :disabled="savingTask || !!loadingTaskId"
            @click="deleteTask(selectedTask)"
          >
            Delete Task
          </v-btn> -->
          <v-spacer />

          <v-btn depressed @click="closeTaskDialog">Close</v-btn>

          <v-btn
            v-if="taskDialogIsCreate"
            color="primary"
            depressed
            :loading="savingTask"
            :disabled="savingTask || !taskDialogValid"
            @click="createTaskFromDialog"
          >
            Create
          </v-btn>

          <v-btn
            v-else-if="canEditTaskDraft"
            color="primary"
            depressed
            :loading="savingTask"
            :disabled="savingTask"
            @click="saveTaskEdits"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { portalsApp } from "@/firebase";
import firebase from "@/plugins/firebase";
import MonthlyAccountingInvoiceReport from "@/components/MonthlyAccountingInvoiceReport";
import { can } from "@/services/auth";

export default {
  components: { MonthlyAccountingInvoiceReport },

  firestore: {
    reminders: portalsApp
      .firestore()
      .collection("admin")
      .doc("reminders"),
    adminNotifications: portalsApp
      .firestore()
      .collection("adminNotifications")
      .where("archived", "==", false)
      .orderBy("dateAdded", "desc"),
  },

  data: () => ({
    reminders: "",
    adminNotifications: [],
    newItem: { text: "" },

    // tasks
    tasksMyOpen: [],
    tasksAllOpen: [],
    tasksAllCompleted: [],
    showCompletedTasks: false,
    loadingTaskId: "",

    // staff
    staffList: [],
    selectedStaffUid: "",

    // dialog state
    taskDialogOpen: false,
    taskDialogIsCreate: false,
    taskDialogValid: false,
    savingTask: false,

    // selected task (for edit/view)
    selectedTask: null,

    // draft model used for BOTH create + edit
    taskDraft: {
      title: "",
      instructions: "",
      dueDate: null, // Timestamp or null
      assignedToUid: "",
      assignedToName: "",
    },

    // due date picker
    dueMenu: false,
    dueDatePicker: null, // "YYYY-MM-DD"

    // attachments
    attachmentFiles: [],
    uploadingAttachments: false,
  }),

  computed: {
    currentUser() {
      return this.$store.state.user || null;
    },
    currentUid() {
      return (this.currentUser && this.currentUser.uid) || "";
    },
    currentName() {
      return (
        (this.currentUser &&
          this.currentUser.staff &&
          (this.currentUser.staff.fullName ||
            this.currentUser.staff.name ||
            this.currentUser.staff.initials)) ||
        ""
      );
    },

    canUseTaskManager() {
      return can(this.currentUser, this.$store, "useTaskManager");
    },

    staffOptions() {
      return (this.staffList || [])
        .filter((s) => !!s)
        .map((s) => ({
          uid: s.uid || s.id,
          fullName: s.fullName || s.email || "(No name)",
          email: s.email || "",
          role: s.role || "",
          title: s.title || "",
        }))
        .filter((x) => !!x.uid);
    },

    todayYmd() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    taskDraftDueDisplay() {
      return this.taskDraft && this.taskDraft.dueDate
        ? this.fmtDate(this.taskDraft.dueDate)
        : "";
    },

    isAssigneeOfSelectedTask() {
      return (
        !!this.selectedTask &&
        this.selectedTask.assignedToUid &&
        this.selectedTask.assignedToUid === this.currentUid
      );
    },

    canEditTaskDraft() {
      if (this.taskDialogIsCreate) return this.canUseTaskManager;
      return this.canUseTaskManager;
    },

    taskDialogModeLabel() {
      if (this.taskDialogIsCreate) return "Create";
      if (this.canEditTaskDraft) return "Edit";
      if (this.isAssigneeOfSelectedTask) return "Read-only (Assignee)";
      return "Read-only";
    },

    canUploadAttachments() {
      if (this.taskDialogIsCreate) return true; // upload after create
      return this.canUseTaskManager || this.isAssigneeOfSelectedTask;
    },

    taskAttachments() {
      const a = (this.selectedTask && this.selectedTask.attachments) || [];
      return Array.isArray(a) ? a : [];
    },
  },

  watch: {
    "$store.state.user"() {
      this.rebindTasks();
      this.rebindStaffList();
      if (!this.canUseTaskManager) this.disableCompletedMode();
    },
    canUseTaskManager() {
      this.rebindTasks();
      this.rebindStaffList();
      if (!this.canUseTaskManager) this.disableCompletedMode();
    },

    selectedStaffUid(uid) {
      const s = (this.staffOptions || []).find((x) => x.uid === uid);
      this.taskDraft.assignedToUid = uid || "";
      this.taskDraft.assignedToName = s ? s.fullName : "";
    },

    async showCompletedTasks(val) {
      if (!this.canUseTaskManager) {
        this.showCompletedTasks = false;
        return;
      }
      if (val) await this.bindCompletedTasks();
      else await this.unbindCompletedTasks();
    },
  },

  async mounted() {
    await this.rebindTasks();
    await this.rebindStaffList();
  },

  methods: {
    asDate(val) {
      if (!val) return null;
      if (typeof val.toDate === "function") return val.toDate();
      if (val.seconds && typeof val.seconds === "number")
        return new Date(val.seconds * 1000);
      if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const [y, m, d] = val.split("-");
        return new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0);
      }
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    },

    fmtDate(val) {
      const d = this.asDate(val);
      if (!d) return "";
      return this.formatDate(d);
    },

    toYmd(val) {
      const d = this.asDate(val);
      if (!d) return "";
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    async rebindTasks() {
      if (!this.currentUid) return;

      try {
        this.$unbind("tasksMyOpen");
      } catch (e) {}
      try {
        this.$unbind("tasksAllOpen");
      } catch (e) {}

      const myQ = portalsApp
        .firestore()
        .collection("tasks")
        .where("assignedToUid", "==", this.currentUid)
        .where("status", "==", "open")
        .orderBy("assignedAt", "desc");

      await this.$bind("tasksMyOpen", myQ);

      if (this.canUseTaskManager) {
        const allQ = portalsApp
          .firestore()
          .collection("tasks")
          .where("status", "==", "open")
          .orderBy("assignedAt", "desc");

        await this.$bind("tasksAllOpen", allQ);
      } else {
        this.tasksAllOpen = [];
      }

      if (!this.canUseTaskManager) {
        await this.unbindCompletedTasks();
      } else if (this.showCompletedTasks) {
        await this.bindCompletedTasks();
      }
    },

    async bindCompletedTasks() {
      if (!this.canUseTaskManager) return;

      try {
        this.$unbind("tasksAllCompleted");
      } catch (e) {}

      const allCompletedQ = portalsApp
        .firestore()
        .collection("tasks")
        .where("status", "==", "completed")
        .orderBy("completedAt", "desc");

      await this.$bind("tasksAllCompleted", allCompletedQ);
    },

    async unbindCompletedTasks() {
      try {
        this.$unbind("tasksAllCompleted");
      } catch (e) {}
      this.tasksAllCompleted = [];
    },

    disableCompletedMode() {
      this.showCompletedTasks = false;
      this.unbindCompletedTasks();
    },

    async rebindStaffList() {
      if (!this.canUseTaskManager) {
        this.staffList = [];
        return;
      }
      try {
        this.$unbind("staffList");
      } catch (e) {}
      const q = portalsApp.firestore().collection("staff");
      await this.$bind("staffList", q);
    },

    openCreateTask() {
      this.taskDialogOpen = true;
      this.taskDialogIsCreate = true;
      this.selectedTask = null;

      this.taskDraft = {
        title: "",
        instructions: "",
        dueDate: null,
        assignedToUid: "",
        assignedToName: "",
      };

      this.selectedStaffUid = "";
      this.attachmentFiles = [];

      this.dueDatePicker = this.todayYmd;
      this.onPickDueDate(this.dueDatePicker);
    },

    openTask(t) {
      if (!t || !t.id) return;

      this.taskDialogOpen = true;
      this.taskDialogIsCreate = false;
      this.selectedTask = t;

      this.taskDraft = {
        title: t.title || "",
        instructions: t.instructions || "",
        dueDate: t.dueDate || null,
        assignedToUid: t.assignedToUid || "",
        assignedToName: t.assignedToName || "",
      };

      this.selectedStaffUid = this.taskDraft.assignedToUid || "";
      this.dueDatePicker = this.toYmd(this.taskDraft.dueDate) || this.todayYmd;
      this.attachmentFiles = [];
    },

    closeTaskDialog() {
      this.taskDialogOpen = false;
      this.taskDialogIsCreate = false;
      this.selectedTask = null;
      this.savingTask = false;
      this.uploadingAttachments = false;
      this.attachmentFiles = [];
      this.dueMenu = false;
    },

    onPickDueDate(val) {
      this.dueDatePicker = val;
      this.dueMenu = false;

      if (!val) {
        this.taskDraft.dueDate = null;
        return;
      }

      const [yyyy, mm, dd] = val.split("-");
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), 0, 0, 0);
      this.taskDraft.dueDate = firebase.firestore.Timestamp.fromDate(d);
    },

    clearDueDate() {
      this.taskDraft.dueDate = null;
      this.dueDatePicker = null;
    },

    async createTaskFromDialog() {
      if (!this.canUseTaskManager) return;
      if (!this.$refs.taskDialogForm || !this.$refs.taskDialogForm.validate())
        return;

      this.savingTask = true;

      try {
        const docRef = await portalsApp
          .firestore()
          .collection("tasks")
          .add({
            title: this.taskDraft.title || "",
            instructions: this.taskDraft.instructions || "",
            dueDate: this.taskDraft.dueDate || null,
            status: "open",

            assignedToUid: this.taskDraft.assignedToUid || "",
            assignedToName: this.taskDraft.assignedToName || "",

            assignedByUid: this.currentUid,
            assignedByName: this.currentName,

            assignedAt: firebase.firestore.FieldValue.serverTimestamp(),

            completedAt: null,
            completedByUid: "",
            completedByName: "",

            attachments: [],
          });

        this.taskDialogIsCreate = false;

        this.selectedTask = {
          id: docRef.id,
          title: this.taskDraft.title,
          instructions: this.taskDraft.instructions,
          dueDate: this.taskDraft.dueDate,
          status: "open",
          assignedToUid: this.taskDraft.assignedToUid,
          assignedToName: this.taskDraft.assignedToName,
          assignedByUid: this.currentUid,
          assignedByName: this.currentName,
          attachments: [],
        };

        this.$toast.success("Task created", { timeout: 1500 });

        if (this.attachmentFiles && this.attachmentFiles.length) {
          await this.uploadAttachments();
        }
      } catch (e) {
        this.$toast.error(e && e.message ? e.message : "Failed to create task");
      } finally {
        this.savingTask = false;
      }
    },

    async saveTaskEdits() {
      if (!this.canUseTaskManager) return;
      if (!this.selectedTask || !this.selectedTask.id) return;

      if (!this.taskDraft.title || !this.taskDraft.title.trim())
        return this.$toast.error("Title is required");
      if (!this.taskDraft.assignedToUid)
        return this.$toast.error("Assigned to is required");

      this.savingTask = true;

      try {
        await portalsApp
          .firestore()
          .collection("tasks")
          .doc(this.selectedTask.id)
          .update({
            title: this.taskDraft.title || "",
            instructions: this.taskDraft.instructions || "",
            dueDate: this.taskDraft.dueDate || null,
            assignedToUid: this.taskDraft.assignedToUid || "",
            assignedToName: this.taskDraft.assignedToName || "",

            dateUpdated: firebase.firestore.FieldValue.serverTimestamp(),
            updatedByUid: this.currentUid,
            updatedByName: this.currentName,
          });

        this.selectedTask.title = this.taskDraft.title;
        this.selectedTask.instructions = this.taskDraft.instructions;
        this.selectedTask.dueDate = this.taskDraft.dueDate;
        this.selectedTask.assignedToUid = this.taskDraft.assignedToUid;
        this.selectedTask.assignedToName = this.taskDraft.assignedToName;

        this.$toast.success("Task updated", { timeout: 1500 });
      } catch (e) {
        this.$toast.error(
          e && e.message ? e.message : "Failed to save changes",
        );
      } finally {
        this.savingTask = false;
      }
    },

    async completeTask(t) {
      if (!t || !t.id) return;

      this.loadingTaskId = t.id;

      try {
        const completedAt = firebase.firestore.Timestamp.now();
        const dateAdded = new Date().toISOString().substring(0, 10);

        await portalsApp
          .firestore()
          .collection("tasks")
          .doc(t.id)
          .update({
            status: "completed",
            completedAt,
            completedByUid: this.currentUid,
            completedByName: this.currentName,
          });

        await portalsApp
          .firestore()
          .collection("adminNotifications")
          .add({
            text: `Task Completed: ${t.title ||
              "(Untitled task)"} • ${t.assignedToName ||
              "Unknown"} • ${this.formatDate(completedAt.toDate())}`,
            archived: false,
            dateAdded,
            type: "taskCompleted",
            taskId: t.id,
            taskTitle: t.title || "",
            assignedToUid: t.assignedToUid || "",
            assignedToName: t.assignedToName || "",
            completedByUid: this.currentUid,
            completedByName: this.currentName,
            completedAt,
          });

        this.$toast.success("Task completed", { timeout: 1500 });

        if (this.selectedTask && this.selectedTask.id === t.id) {
          this.selectedTask.status = "completed";
          this.selectedTask.completedAt = completedAt;
          this.selectedTask.completedByUid = this.currentUid;
          this.selectedTask.completedByName = this.currentName;
        }
      } catch (e) {
        console.error("completeTask error:", e);
        this.$toast.error(
          e && e.message ? e.message : "Failed to complete task",
        );
      } finally {
        this.loadingTaskId = "";
      }
    },
    async cancelTask(t) {
      if (!t || !t.id) return;
      if (!confirm(`Cancel task "${t.title || "(Untitled task)"}"?`)) return;

      this.loadingTaskId = t.id;

      try {
        await portalsApp
          .firestore()
          .collection("tasks")
          .doc(t.id)
          .update({
            status: "cancelled",
            cancelledAt: firebase.firestore.FieldValue.serverTimestamp(),
            cancelledByUid: this.currentUid,
            cancelledByName: this.currentName,
          });

        await portalsApp
          .firestore()
          .collection("adminNotifications")
          .add({
            text: `Task Cancelled: ${t.title ||
              "(Untitled task)"} • ${t.assignedToName ||
              "Unknown"} • ${new Date().toLocaleString()}`,
            archived: false,
            dateAdded: new Date().toISOString().substring(0, 10),
            type: "taskCancelled",
            taskId: t.id,
            taskTitle: t.title || "",
            assignedToUid: t.assignedToUid || "",
            assignedToName: t.assignedToName || "",
            cancelledByUid: this.currentUid,
            cancelledByName: this.currentName,
          });

        this.$toast.success("Task cancelled", { timeout: 1500 });

        if (this.selectedTask && this.selectedTask.id === t.id) {
          this.selectedTask.status = "cancelled";
        }

        this.closeTaskDialog();
      } catch (e) {
        this.$toast.error(e && e.message ? e.message : "Failed to cancel task");
      } finally {
        this.loadingTaskId = "";
      }
    },

    async deleteTask(t) {
      if (!t || !t.id) return;
      if (
        !confirm(
          `Delete task "${t.title ||
            "(Untitled task)"}"? This cannot be undone.`,
        )
      )
        return;

      this.loadingTaskId = t.id;

      try {
        await portalsApp
          .firestore()
          .collection("tasks")
          .doc(t.id)
          .delete();

        this.$toast.success("Task deleted", { timeout: 1500 });
        this.closeTaskDialog();
      } catch (e) {
        this.$toast.error(e && e.message ? e.message : "Failed to delete task");
      } finally {
        this.loadingTaskId = "";
      }
    },
    async uploadAttachments() {
      if (this.taskDialogIsCreate) return;
      if (!this.selectedTask || !this.selectedTask.id) return;
      if (!this.attachmentFiles || !this.attachmentFiles.length) return;
      if (!this.canUploadAttachments) return;

      this.uploadingAttachments = true;

      try {
        const bucket = portalsApp.storage();
        const now = Date.now();

        const uploaded = [];

        for (let i = 0; i < this.attachmentFiles.length; i++) {
          const f = this.attachmentFiles[i];
          if (!f) continue;

          const safeName = String(f.name || "file").replace(/[^\w.\- ]/g, "_");
          const path = `taskAttachments/${this.selectedTask.id}/${now}_${i}_${safeName}`;
          const ref = bucket.ref().child(path);

          await ref.put(f);
          const url = await ref.getDownloadURL();

          uploaded.push({
            name: f.name || safeName,
            path,
            url,
            uploadedAt: firebase.firestore.Timestamp.now(),
            uploadedByUid: this.currentUid,
            uploadedByName: this.currentName,
          });
        }

        if (uploaded.length) {
          await portalsApp
            .firestore()
            .collection("tasks")
            .doc(this.selectedTask.id)
            .update({
              attachments: firebase.firestore.FieldValue.arrayUnion(
                ...uploaded,
              ),
            });

          const current = Array.isArray(this.selectedTask.attachments)
            ? this.selectedTask.attachments
            : [];
          this.selectedTask.attachments = current.concat(uploaded);

          this.$toast.success("Attachments uploaded", { timeout: 1500 });
          this.attachmentFiles = [];
        }
      } catch (e) {
        this.$toast.error(
          e && e.message ? e.message : "Failed to upload attachments",
        );
      } finally {
        this.uploadingAttachments = false;
      }
    },

    async archiveAllNotifications() {
      if (!this.adminNotifications || !this.adminNotifications.length) return;
      this.$store.state.loading = true;
      let total = this.adminNotifications.length;
      let count = 0;

      this.adminNotifications.forEach((i) => {
        if (i == null || i == undefined || !i.id) return;

        portalsApp
          .firestore()
          .collection("adminNotifications")
          .doc(i.id)
          .update({
            archived: true,
            dateArchived: new Date().toISOString().substring(0, 10),
          })
          .then(() => {
            count = count + 1;
            if (total == count) {
              this.$store.state.loading = false;
              this.$toast.success("All Notifications Archived");
            }
          });
      });
    },

    async archiveNotification(item) {
      if (item == null || item == undefined || !item.id) return;

      this.$store.state.loading = true;

      portalsApp
        .firestore()
        .collection("adminNotifications")
        .doc(item.id)
        .update({
          archived: true,
          dateArchived: new Date().toISOString().substring(0, 10),
        })
        .then(() => {
          this.$store.state.loading = false;
          this.$toast.success("Notification Archived", { timeout: 1500 });
        });
    },
  },
};
</script>

<style>
tr {
  cursor: pointer;
}
.text-sm {
  font-size: 14px;
}
</style>
